import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import LFCButton from '_components/inputs/LFCButton';
import {fmtThreeCommas, fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {downloadFromUrl} from '_logics/LFCSourceUtil';
import {usePgHistoryProfiles} from '_logics/usePgHistoryProfiles';
import {matchProfile} from '_logics/pghistoryProfileMatch';
import {
  getSnapshotFiles,
  getSnapshotPreview,
  getSnapshotUrl,
  getSnapshotVersions,
  type SnapshotFile,
  type SnapshotVersion
} from '_logics/LFCPgHistorySnapshotUtil';
import DiffDisplay from './DiffDisplay';
import PaneHeader from './PaneHeader';
import PathTree from './PathTree';
import SourceViewer from './SourceViewer';
import SplitPane, {MAIN_AREA_HEIGHT} from './SplitPane';

const PREVIEW_LIMIT = 512 * 1024;
/** 版一覧の幅（右ペインの左側に固定） */
const VERSION_LIST_WIDTH = 260;
const displayDate = (value: string | null | undefined) => fmtYYYYMMDDHHmmss(value) ?? '—';
const baseName = (path: string) => path.split(/[/:\\]/).pop() || path;
type Preview =
  | {status: 'loading'}
  | {status: 'ready'; text: string; encoding: string}
  | {status: 'unavailable'};
type ViewMode = 'content' | 'diff';

/** ファイル一覧を続きのページがなくなるまで全件取得する */
const loadAllFiles = async (
  ctx: Parameters<typeof getSnapshotFiles>[0],
  device: string,
  isStale: () => boolean
): Promise<SnapshotFile[]> => {
  const all: SnapshotFile[] = [];
  for (;;) {
    const after = all[all.length - 1]?.file_path ?? '';
    const {rows, hasMore} = await getSnapshotFiles(ctx, device, after);
    if (isStale()) return all;
    if (after && rows.some(row => row.file_path <= after))
      throw new Error('Invalid snapshot file cursor');
    all.push(...rows);
    if (!hasMore || !rows.length) return all;
  }
};

export default function Snapshots({selectedDevice: device}: {selectedDevice: string}) {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const ctx = useMemo(() => ({t, snack: enqueueSnackbar}), [t, enqueueSnackbar]);
  const [files, setFiles] = useState<SnapshotFile[]>([]);
  const [filePath, setFilePath] = useState('');
  const [versions, setVersions] = useState<SnapshotVersion[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<ViewMode>('content');
  // 版ごとのプレビュー（snapshot_id 単位でキャッシュし、差分表示で再取得しない）
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const requested = useRef(new Set<string>());
  const [filesBusy, setFilesBusy] = useState(false);
  const [versionsBusy, setVersionsBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [moreVersions, setMoreVersions] = useState(false);
  const [versionRetry, setVersionRetry] = useState(0);
  const [error, setError] = useState('');
  const fileEpoch = useRef(0);
  const versionEpoch = useRef(0);
  // 分類ルール（差分で先頭の除外行を変更として扱わないために使う）
  const profiles = usePgHistoryProfiles();

  useEffect(() => {
    const epoch = ++fileEpoch.current;
    ++versionEpoch.current;
    setFiles([]);
    setFilePath('');
    setVersions([]);
    setSelectedId(null);
    setMoreVersions(false);
    setError('');
    setFilesBusy(!!device);
    if (device) {
      loadAllFiles(ctx, device, () => epoch !== fileEpoch.current)
        .then(rows => {
          if (epoch === fileEpoch.current) setFiles(rows);
        })
        .catch(() => {
          if (epoch === fileEpoch.current) setError('snapshot.loadFailed');
        })
        .finally(() => {
          if (epoch === fileEpoch.current) setFilesBusy(false);
        });
    }
    return () => {
      ++fileEpoch.current;
    };
  }, [ctx, device]);

  useEffect(() => {
    const epoch = ++versionEpoch.current;
    setVersions([]);
    setSelectedId(null);
    setMode('content');
    setPreviews({});
    requested.current = new Set();
    setMoreVersions(false);
    setDownloadBusy(false);
    setError('');
    setVersionsBusy(!!filePath);
    if (filePath) {
      getSnapshotVersions(ctx, device, filePath)
        .then(({rows, hasMore}) => {
          if (epoch !== versionEpoch.current) return;
          setVersions(rows);
          // 最新の版を選んで内容をすぐ表示する
          setSelectedId(rows[0]?.snapshot_id ?? null);
          setMoreVersions(hasMore);
        })
        .catch(() => {
          if (epoch === versionEpoch.current) setError('snapshot.loadFailed');
        })
        .finally(() => {
          if (epoch === versionEpoch.current) setVersionsBusy(false);
        });
    }
    return () => {
      ++versionEpoch.current;
    };
  }, [ctx, device, filePath, versionRetry]);

  // サーバーの変更検知（先頭から連続して header_pattern に一致する行を除く）と同じ行を差分でも変更として扱わない。
  // ルールは Python の正規表現なので、JavaScript で解釈できないものは使わない
  const headerPattern = useMemo(() => {
    const pattern = filePath ? matchProfile(profiles, device, filePath)?.header_pattern : null;
    if (!pattern) return undefined;
    try {
      return new RegExp(pattern);
    } catch {
      return undefined;
    }
  }, [profiles, device, filePath]);

  const index = versions.findIndex(row => row.snapshot_id === selectedId);
  const selected = index >= 0 ? versions[index] : undefined;
  // 一覧は新しい順なので、1つ前の版は次の行。次のページにしかない場合は差分を選べない
  const previous = index >= 0 ? versions[index + 1] : undefined;
  const viewMode: ViewMode = mode === 'diff' && previous ? 'diff' : 'content';

  useEffect(() => {
    const epoch = versionEpoch.current;
    const load = (row: SnapshotVersion | undefined) => {
      if (!row || requested.current.has(row.snapshot_id)) return;
      const id = row.snapshot_id;
      requested.current.add(id);
      const put = (preview: Preview) => {
        if (epoch === versionEpoch.current) setPreviews(current => ({...current, [id]: preview}));
      };
      if (row.size_bytes > PREVIEW_LIMIT) {
        put({status: 'unavailable'});
        return;
      }
      put({status: 'loading'});
      getSnapshotPreview(ctx, device, id)
        .then(result => put({status: 'ready', ...result}))
        .catch(() => put({status: 'unavailable'}));
    };
    load(selected);
    if (viewMode === 'diff') load(previous);
  }, [ctx, device, selected, previous, viewMode]);

  const loadMoreVersions = async () => {
    const last = versions[versions.length - 1];
    if (!last) return;
    const epoch = versionEpoch.current;
    setVersionsBusy(true);
    setError('');
    try {
      const {rows, hasMore} = await getSnapshotVersions(ctx, device, filePath, last);
      if (epoch !== versionEpoch.current) return;
      if (
        rows.some(
          row =>
            row.collected_at > last.collected_at ||
            (row.collected_at === last.collected_at && row.snapshot_id >= last.snapshot_id)
        )
      )
        throw new Error('Invalid snapshot version cursor');
      setVersions(current => [...current, ...rows]);
      setMoreVersions(hasMore);
    } catch {
      if (epoch === versionEpoch.current) setError('snapshot.loadFailed');
    } finally {
      if (epoch === versionEpoch.current) setVersionsBusy(false);
    }
  };
  const download = async (row: SnapshotVersion) => {
    const epoch = versionEpoch.current;
    setDownloadBusy(true);
    setError('');
    try {
      const result = await getSnapshotUrl(ctx, device, row.snapshot_id);
      if (epoch === versionEpoch.current) downloadFromUrl(result.url, result.file_name);
    } catch {
      if (epoch === versionEpoch.current) setError('snapshot.downloadFailed');
    } finally {
      if (epoch === versionEpoch.current) setDownloadBusy(false);
    }
  };
  const treeItems = useMemo(
    () => files.map(row => ({path: row.file_path, meta: displayDate(row.collected_at)})),
    [files]
  );
  const openFile = (path: string) => {
    setFilePath(path);
    setVersionRetry(value => value + 1);
  };

  if (!device) return <Alert severity="info">{t('端末を選択してください')}</Alert>;
  if (!files.length)
    return (
      <Stack spacing={2} sx={{pb: 2}}>
        <Alert severity="info">{t('snapshot.retentionRule')}</Alert>
        {error && <Alert severity="error">{t(error)}</Alert>}
        {filesBusy && <CircularProgress aria-label={t('snapshot.loading')} size={24} />}
        {!filesBusy && !error && <Alert severity="info">{t('snapshot.noFiles')}</Alert>}
      </Stack>
    );
  const file = files.find(row => row.file_path === filePath);
  const fileName = baseName(filePath);
  const selectedPreview = selected && previews[selected.snapshot_id];
  const previousPreview = previous && previews[previous.snapshot_id];

  // 画面で表示できない版も、原本は取得できるようにする（表示できるときはビューアー・差分の見出しから取得する）
  const renderUnavailable = () => (
    <Alert
      severity="info"
      action={
        selected && (
          <LFCButton
            size="small"
            color="primary"
            disabled={downloadBusy}
            onClick={() => download(selected)}
          >
            {t('snapshot.download')}
          </LFCButton>
        )
      }
    >
      {t('snapshot.previewUnavailable')}
    </Alert>
  );

  const renderContent = () => {
    if (!selected) return null;
    if (viewMode === 'diff' && previous) {
      if (selectedPreview?.status === 'unavailable' || previousPreview?.status === 'unavailable')
        return renderUnavailable();
      return (
        <DiffDisplay
          beforeContent={previousPreview?.status === 'ready' ? previousPreview.text : ''}
          afterContent={selectedPreview?.status === 'ready' ? selectedPreview.text : ''}
          fileName={fileName}
          maxHeight="none"
          loading={previousPreview?.status !== 'ready' || selectedPreview?.status !== 'ready'}
          onDownloadBefore={() => download(previous)}
          onDownloadAfter={() => download(selected)}
          ignoreLeadingLinePattern={headerPattern}
        />
      );
    }
    if (selectedPreview?.status === 'unavailable') return renderUnavailable();
    return (
      <SourceViewer
        hideFileName
        content={selectedPreview?.status === 'ready' ? selectedPreview.text : null}
        fileName={fileName}
        filePath={filePath}
        fileSize={selected.size_bytes}
        downloadUrl={null}
        loading={selectedPreview?.status !== 'ready'}
        commitDate={selected.collected_at}
        onDownload={() => download(selected)}
      />
    );
  };

  return (
    <Box sx={{height: MAIN_AREA_HEIGHT}}>
      <SplitPane
        left={
          <PathTree
            label={t('snapshot.files')}
            items={treeItems}
            selectedPath={filePath || null}
            onSelect={openFile}
          />
        }
      >
        {!filePath ? (
          <Typography color="text.secondary">{t('snapshot.selectFile')}</Typography>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minHeight: 0,
              pr: 1.5,
              pb: 1
            }}
          >
            <PaneHeader
              title={filePath}
              meta={
                file &&
                t('snapshot.fileMeta', {
                  changed: displayDate(file.collected_at),
                  seen: displayDate(file.last_seen_at)
                })
              }
              note={t('snapshot.retentionRule')}
            />
            {error && (
              <Alert severity="error" sx={{flexShrink: 0}}>
                {t(error)}
              </Alert>
            )}
            {versionsBusy && versions.length === 0 && (
              <CircularProgress aria-label={t('snapshot.loading')} size={24} />
            )}
            {!versionsBusy && versions.length === 0 && !error && (
              <Alert severity="info">{t('snapshot.noVersions')}</Alert>
            )}
            {!versionsBusy && versions.length === 0 && error === 'snapshot.loadFailed' && (
              <Box>
                <LFCButton color="primary" onClick={() => setVersionRetry(value => value + 1)}>
                  {t('snapshot.retry')}
                </LFCButton>
              </Box>
            )}
            {versions.length > 0 && (
              <Box sx={{flex: 1, minHeight: 0, display: 'flex', gap: 1}}>
                <Box
                  sx={{
                    width: VERSION_LIST_WIDTH,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <List
                    dense
                    disablePadding
                    aria-label={t('snapshot.versions')}
                    sx={{flex: 1, minHeight: 0, overflow: 'auto'}}
                  >
                    {versions.map(row => {
                      const isSelected = row.snapshot_id === selectedId;
                      return (
                        <ListItem key={row.snapshot_id} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            aria-current={isSelected ? 'true' : undefined}
                            onClick={() => setSelectedId(row.snapshot_id)}
                            sx={{py: 0.25}}
                          >
                            <ListItemText
                              primary={displayDate(row.collected_at)}
                              secondary={`${fmtThreeCommas(row.size_bytes)} B`}
                              primaryTypographyProps={{variant: 'body2'}}
                              secondaryTypographyProps={{variant: 'caption'}}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                  {moreVersions && (
                    <Box sx={{p: 1, borderTop: 1, borderColor: 'divider'}}>
                      <LFCButton
                        size="small"
                        color="secondary"
                        fullWidth
                        disabled={versionsBusy}
                        onClick={loadMoreVersions}
                      >
                        {t('snapshot.moreVersions')}
                      </LFCButton>
                    </Box>
                  )}
                </Box>
                <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      color="primary"
                      value={viewMode}
                      aria-label={t('snapshot.viewMode')}
                      onChange={(_, next: ViewMode | null) => next && setMode(next)}
                    >
                      <ToggleButton value="content" sx={{px: 1.5}}>
                        {t('snapshot.modeContent')}
                      </ToggleButton>
                      <ToggleButton value="diff" disabled={!previous} sx={{px: 1.5}}>
                        {t('snapshot.modeDiff')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {viewMode === 'content' && selectedPreview?.status === 'ready' && (
                      <Typography variant="caption" color="text.secondary">
                        {`${t('snapshot.previewEncoding')}: ${selectedPreview.encoding}`}
                      </Typography>
                    )}
                    {viewMode === 'diff' && headerPattern && (
                      <Typography variant="caption" color="text.secondary">
                        {t('snapshot.headerIgnored', {pattern: headerPattern.source})}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{flex: 1, minHeight: 0}}>{renderContent()}</Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </SplitPane>
    </Box>
  );
}
